import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { HeaderContext } from "./headerStateContext";
import { useQueryClient } from "@tanstack/react-query";
import { Guest } from "../api/contracts/category-ads";
import { AgeType, BedCapacityType } from "../api/contracts/reservation";
import { LanguageCode } from "../api/contracts/language-settings";
import { WBEAuthSessionResponse } from "../api/contracts/wbe-auth";
import {
  GetWBEConfigResponse,
  WBEMaxConfigGuests,
} from "../api/contracts/site-config";
import { getBedTypeForAge } from "../utils/bedTypeUtils";
import { resizeGuestList } from "../utils/resizeGuestList";
import { createDefaultGuests } from "../utils/defaultGuests";
import { useConfiguration } from "./ConfigurationProvider";
import { BookingAvailability } from "../../../api-contracts/sites/wbe/booking-availability";
import { endOfWeek, startOfWeek } from "date-fns";
import { useGetBookingAvailability } from "../api/api-hooks/booking-availability";
import { useWBEUpdateLanguage } from "../api/api-hooks/wbe-auth";
import { formatDateForAPI } from "../utils/apiUtils";
import i18n from "../lib/i18n";
import { hasTodayArrivalParam } from "../utils/arrivalParams";
import { toasty } from "../components/Toast";
import { useTranslation } from "react-i18next";
import {
  getBrowserLanguageCode,
  pickOfferedLanguage,
} from "../utils/browserLanguage";
import { getI18nLanguage, getLanguageCode } from "../utils/language";
import { useBookingCodeStore } from "../stores/bookingCodeStore";
import { NO_AUTO_SEARCH_PARAM } from "../utils/logoNavigation";
import { useAdFormat } from "../hooks/useAdFormat";
import { URL_PARAMS, URL_PARAM_ORDER } from "../utils/urlParams";

export interface SearchUnit {
  id: string;
  guests: Guest[];
}

export interface HeaderContextType {
  selectedLanguage: LanguageCode;
  /**
   * Transient trigger: setting to `true` asks the header's calendar popover
   * to open (e.g. NoResults' "Ändra datum"). Consumers that own the popover
   * clear it back to `false` once they've handled it — it is not a mirror of
   * the popover's actual open/close state (that lives inside BookingSearch).
   */
  openCalendarRequest: boolean;
  /**
   * Whether the mobile search sheet is open. Lives here rather than in the
   * landing page because switching ad format swaps Landing for ActivityLanding
   * (see LandingSwitch), and page-local state would take the open sheet with it.
   */
  showMobileSearch: boolean;
  currentMonth: Date;
  selectedStartDate: Date | null;
  selectedEndDate: Date | null;
  hasTodayArrivalDeeplink: boolean;
  units: SearchUnit[];
  activeUnitId: string;
  guests: Guest[]; // active unit guests (legacy)
  searchInitiated: boolean;
  /** True once the one-shot URL bootstrap has run. Consumers that rebuild header state from
   *  elsewhere (the booking flow's plan restore) wait for this so they never race it. */
  isUrlInitialized: boolean;
  /**
   * The search string the header would write for these values: header keys rewritten, every
   * other key preserved, stable order, no leading `?`. Lets the booking flow push a complete URL
   * before it calls the setters, so the new history entry already carries the new dates.
   */
  serializeSearchParams: (input: {
    base: string;
    dates: { arrival: string; departure: string } | null;
    guests: Guest[];
  }) => string;
  /**
   * Re-hydrate dates and ONE unit's guests from a search string — what browser Back needs after
   * the flow pushed a URL with different dates. Never touches `searchInitiated` or the URL.
   */
  applySearchParams: (search: string, unitId: string) => void;
  wbeConfig: GetWBEConfigResponse | undefined;
  isConfigLoading: boolean;
  bookingAvailability: BookingAvailability | undefined;
  /** Site-wide availability request in flight — first read or refetch. */
  bookingAvailabilityFetching: boolean;
  getMaxGuestCount: (ageType: AgeType) => number;
  getBedTypeForAge: (age: number) => BedCapacityType;
  setSelectedLanguage: (language: LanguageCode) => void;
  applySelectedLanguage: (language: LanguageCode) => void;
  setOpenCalendarRequest: (open: boolean) => void;
  setShowMobileSearch: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentMonth: (date: Date) => void;
  setSelectedStartDate: (date: Date | null) => void;
  setSelectedEndDate: (date: Date | null) => void;
  setGuestCount: (ageType: AgeType, count: number) => void;
  setGuestCountForUnit: (
    unitId: string,
    ageType: AgeType,
    count: number,
  ) => void;
  updateGuest: (index: number, updates: Partial<Guest>) => void;
  updateGuestForUnit: (
    unitId: string,
    index: number,
    updates: Partial<Guest>,
  ) => void;
  getGuestsByType: (ageType: AgeType) => Guest[];
  getGuestCount: (ageType: AgeType) => number;
  setActiveUnitId: (unitId: string) => void;
  addUnit: () => void;
  removeUnit: (unitId: string) => void;
  replaceUnits: (units: SearchUnit[]) => void;
  setSearchInitiated: (initiated: boolean) => void;
  updateURLParams: () => void;
  resetHeader: () => void;
}



const hasAuthenticatedWBESession = (
  sessions: Array<[unknown, WBEAuthSessionResponse | undefined]>,
) => sessions.some(([, session]) => session?.authenticated === true);

// Defaults for guests created before the WBE config has been fetched (e.g. URL
// initialization). bedCapacityType is recalculated against bedAgeSpanSettings
// once the config loads; age comes from the user-picked dropdown or null.
const DEFAULT_GUEST_CONFIG: Record<AgeType, Omit<Guest, "ageType">> = {
  adult: { bedCapacityType: "full" },
  teenager: { bedCapacityType: "full" },
  child: { bedCapacityType: "full" },
  infant: { bedCapacityType: "cot" },
};

// oxlint-disable-next-line react/only-export-components -- tests mock this module path for the helper
export const createUnitId = () =>
  `unit_${Date.now()}_${Math.random().toString(16).slice(2)}`;
const createId = createUnitId;

const AGE_TYPE_TO_CONFIG_KEY: Record<AgeType, keyof WBEMaxConfigGuests> = {
  adult: "adults",
  teenager: "teenagers",
  child: "children",
  infant: "infants",
};



const isValidDate = (dateString: string): boolean => {
  if (!dateString) return false;
  const date = new Date(dateString);
  return !isNaN(date.getTime()) && /^\d{4}-\d{2}-\d{2}$/.test(dateString);
};

const isValidNumber = (value: string): boolean => {
  const num = parseInt(value);
  return !isNaN(num) && num >= 0;
};

const parseNonNegativeInt = (value: string): number | null => {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const parsed = parseInt(trimmed, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const MAX_COMPACT_GUEST_PARAM_COUNT = 100;

const isBedCapacityType = (
  value: string | undefined,
): value is BedCapacityType =>
  value === "full" || value === "half" || value === "cot";

const isNotInPast = (dateString: string): boolean => {
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date >= today;
};

const isValidDateRange = (
  arrival: string,
  departure: string,
  allowSameDay = false,
): boolean => {
  if (!isValidDate(arrival) || !isValidDate(departure)) return false;
  const arrivalDate = new Date(arrival);
  const departureDate = new Date(departure);
  // Activity searches are single-day complete — from = to is a valid search,
  // while a lodging stay always spans at least one night.
  return allowSameDay ? departureDate >= arrivalDate : departureDate > arrivalDate;
};

const validateUrlDates = (
  arrival: string | null,
  departure: string | null,
  allowSameDay = false,
) => {
  if (!arrival || !departure) {
    return { isValid: false, shouldTriggerSearch: false, warning: null };
  }

  if (!isValidDate(arrival) || !isValidDate(departure)) {
    return {
      isValid: false,
      shouldTriggerSearch: false,
      warning: "warning.invalid_date_format",
    };
  }

  if (!isValidDateRange(arrival, departure, allowSameDay)) {
    return {
      isValid: false,
      shouldTriggerSearch: false,
      warning: "warning.invalid_date_range",
    };
  }

  if (!isNotInPast(arrival)) {
    return {
      isValid: false,
      shouldTriggerSearch: false,
      warning: "warning.arrival_date_in_past",
    };
  }

  return { isValid: true, shouldTriggerSearch: true, warning: null };
};

const parseGuestParams = (paramValue: string, ageType: AgeType): Guest[] => {
  if (!paramValue?.trim()) return [];

  // Accept compact count format for deeplinks like `children=2` / `infants=1`
  // and expand to guest entries with default bed capacity.
  const compactCount = parseNonNegativeInt(paramValue);
  if (compactCount !== null) {
    const safeCompactCount = Math.min(
      compactCount,
      MAX_COMPACT_GUEST_PARAM_COUNT,
    );
    const baseGuest = DEFAULT_GUEST_CONFIG[ageType];
    return Array.from({ length: safeCompactCount }, () => ({
      ...baseGuest,
      ageType,
    })) as Guest[];
  }

  return paramValue
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
    .map((item) => {
      const [ageStr, bedTypeStr] = item.split(":");
      const age = parseNonNegativeInt(ageStr ?? "");
      if (age === null) return null;
      const baseGuest = DEFAULT_GUEST_CONFIG[ageType];
      const bedCapacityType = isBedCapacityType(bedTypeStr)
        ? bedTypeStr
        : baseGuest.bedCapacityType;
      return {
        age,
        bedCapacityType,
        ageType,
      } as Guest;
    })
    .filter((guest): guest is Guest => guest !== null);
};

/** Calendar-day formatting for the URL: the date as the guest picked it, with no timezone
 *  conversion. Exported so a caller writing the same params cannot drift from `updateURLParams`. */
// oxlint-disable-next-line react/only-export-components -- tests mock this module path for the helper
export const formatLocalDate = (date: Date) =>
  `${date.getFullYear()}-${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;

const normalizeDateParam = (
  value: string | null,
): { value: string | null; wasNormalized: boolean } => {
  if (!value) return { value: null, wasNormalized: false };

  const normalized = value.trim().toLowerCase();
  if (normalized === "today") {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return { value: formatLocalDate(today), wasNormalized: true };
  }

  if (normalized === "tomorrow") {
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return { value: formatLocalDate(tomorrow), wasNormalized: true };
  }

  return { value, wasNormalized: false };
};

/**
 * A `?lang=` value, accepted as either a bare subtag or a full code. Returns
 * undefined for anything we do not serve, so an unknown value leaves the
 * browser's own preference in charge rather than forcing English.
 */
const parseUrlLanguage = (value: string): LanguageCode | undefined => {
  const subtag = value.split("-")[0];
  const code = getLanguageCode(subtag);
  return getI18nLanguage(code) === subtag ? code : undefined;
};

const serializeParamsWithStableOrder = (params: URLSearchParams): string => {
  const ordered = new URLSearchParams();
  const knownKeys = new Set<string>(URL_PARAM_ORDER);

  URL_PARAM_ORDER.forEach((key) => {
    params.getAll(key).forEach((value) => ordered.append(key, value));
  });

  const customKeys = Array.from(new Set(Array.from(params.keys())))
    .filter((key) => !knownKeys.has(key))
    .sort((a, b) => a.localeCompare(b));

  customKeys.forEach((key) => {
    params.getAll(key).forEach((value) => ordered.append(key, value));
  });

  return ordered.toString();
};

/** Header keys rewritten for the given values; every other key kept. No leading `?`. */
const writeHeaderParams = (
  base: string,
  values: {
    dates: { arrival: string; departure: string } | null;
    guests: Guest[];
    languageCode: LanguageCode;
    guestTypesConfig: GetWBEConfigResponse["guestTypesConfig"] | undefined;
  },
): string => {
  const params = new URLSearchParams(base);

  if (values.dates) {
    params.set(URL_PARAMS.ARRIVAL, values.dates.arrival);
    params.set(URL_PARAMS.DEPARTURE, values.dates.departure);
  } else {
    params.delete(URL_PARAMS.ARRIVAL);
    params.delete(URL_PARAMS.DEPARTURE);
  }

  params.set(URL_PARAMS.LANG, getI18nLanguage(values.languageCode) || "sv");

  const byType = (type: AgeType) =>
    values.guests.filter((g) => g.ageType === type);
  const adultCount = byType("adult").length;
  const teenagerCount = byType("teenager").length;
  if (adultCount > 0) params.set(URL_PARAMS.ADULTS, adultCount.toString());
  else params.delete(URL_PARAMS.ADULTS);
  if (teenagerCount > 0)
    params.set(URL_PARAMS.TEENAGERS, teenagerCount.toString());
  else params.delete(URL_PARAMS.TEENAGERS);

  // Only guests whose age can be resolved are serialized; see the comment that used to sit in
  // updateURLParams — a 0 fallback would round-trip an out-of-range guest.
  const serializeAged = (
    type: "child" | "infant",
    minAge: number | undefined,
  ) =>
    byType(type)
      .map((g) => {
        const age = g.age ?? minAge;
        return age == null ? null : `${age}:${g.bedCapacityType}`;
      })
      .filter((s): s is string => s !== null)
      .join(",");
  const childrenParam = serializeAged(
    "child",
    values.guestTypesConfig?.child?.minAge,
  );
  if (childrenParam) params.set(URL_PARAMS.CHILDREN, childrenParam);
  else params.delete(URL_PARAMS.CHILDREN);
  const infantsParam = serializeAged(
    "infant",
    values.guestTypesConfig?.infant?.minAge,
  );
  if (infantsParam) params.set(URL_PARAMS.INFANTS, infantsParam);
  else params.delete(URL_PARAMS.INFANTS);

  return serializeParamsWithStableOrder(params);
};

export const HeaderProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { configuration: wbeConfig, isLoading: isConfigLoading } =
    useConfiguration();
  const { isActivity } = useAdFormat();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const updateLanguageMutation = useWBEUpdateLanguage();
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>(() => {
    // Deeplink takes precedence on entry; only read URL once at mount so
    // later state changes are not reverted by stale URL values.
    const params = new URLSearchParams(window.location.search);
    const langParam = params.get(URL_PARAMS.LANG)?.toLowerCase() ?? null;

    // `se` is not a language tag, but the links we have handed out use it.
    const urlLanguage = langParam
      ? (parseUrlLanguage(langParam === "se" ? "sv" : langParam) ?? null)
      : null;

    if (urlLanguage) {
      localStorage.setItem("selectedLanguage", urlLanguage);
      return urlLanguage;
    }

    const storedLanguage = localStorage.getItem(
      "selectedLanguage",
    ) as LanguageCode | null;
    if (storedLanguage) return storedLanguage;

    const browserLanguage = getBrowserLanguageCode();
    localStorage.setItem("selectedLanguage", browserLanguage);
    return browserLanguage;
  });
  const [openCalendarRequest, setOpenCalendarRequest] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [hasTodayArrivalDeeplink] = useState(() =>
    hasTodayArrivalParam(window.location.search),
  );
  const [searchInitiated, setSearchInitiated] = useState(false);
  const [isUrlInitialized, setIsUrlInitialized] = useState(false);
  const hasSyncedInitialUrlRef = useRef(false);
  const languageUpdateRequestIdRef = useRef(0);

  const [units, setUnits] = useState<SearchUnit[]>(() => {
    const id = createId();
    return [{ id, guests: createDefaultGuests() }];
  });
  const [activeUnitId, setActiveUnitId] = useState<string>(() => units[0]!.id);

  const activeUnitIndex = useMemo(() => {
    const idx = units.findIndex((u) => u.id === activeUnitId);
    return idx >= 0 ? idx : 0;
  }, [units, activeUnitId]);

  const guests = useMemo(() => {
    return units[activeUnitIndex]?.guests ?? createDefaultGuests();
  }, [units, activeUnitIndex]);

  // URL bootstrap must know the site module (single-day activity search URLs
  // carry arrival = departure, which lodging validation rejects), so it waits
  // for the configuration and then runs exactly once.
  const hasBootstrappedUrl = useRef(false);
  useEffect(() => {
    if (isConfigLoading || hasBootstrappedUrl.current) return;
    hasBootstrappedUrl.current = true;
    const urlParams = new URLSearchParams(window.location.search);
    const hasCategoryAdDeepLink = !!urlParams.get("categoryAdId");
    // A logo click hands the search back untouched but asks for the start view,
    // so the dates below are restored without the search running on them.
    const suppressAutoSearch = urlParams.get(NO_AUTO_SEARCH_PARAM) === "1";

    const arrivalParam = normalizeDateParam(urlParams.get(URL_PARAMS.ARRIVAL));
    const departureParam = normalizeDateParam(
      urlParams.get(URL_PARAMS.DEPARTURE),
    );
    const arrival = arrivalParam.value;
    const departure = departureParam.value;
    const adults = urlParams.get(URL_PARAMS.ADULTS);
    const teenagers = urlParams.get(URL_PARAMS.TEENAGERS);
    const children = urlParams.get(URL_PARAMS.CHILDREN);
    const infants = urlParams.get(URL_PARAMS.INFANTS);

    // Handle dates
    // - If both arrival and departure exist:
    //   * validate them and auto-correct if invalid
    // - If only one exists and is valid:
    //   * prefill that date field but do NOT auto-correct or trigger search
    let finalArrival: string | null = arrival;
    let finalDeparture: string | null = departure;
    let shouldUpdateURL =
      arrivalParam.wasNormalized || departureParam.wasNormalized;

    if (arrival && departure) {
      const dateValidation = validateUrlDates(
        arrival,
        departure,
        isActivity,
      );

      // If both dates are present but invalid: auto-correct to today/tomorrow
      if (!dateValidation.isValid) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        finalArrival = formatLocalDate(today);
        finalDeparture = formatLocalDate(tomorrow);
        shouldUpdateURL = true;
      }
    }

    // Local midnight, not new Date("YYYY-MM-DD")'s UTC midnight — browsers
    // west of UTC would otherwise search a day before the deep-linked date.
    if (finalArrival && isValidDate(finalArrival)) {
      setSelectedStartDate(new Date(`${finalArrival}T00:00:00`));
    }

    if (finalDeparture && isValidDate(finalDeparture)) {
      setSelectedEndDate(new Date(`${finalDeparture}T00:00:00`));
    }

    const urlGuests: Guest[] = [];

    if (adults !== null && isValidNumber(adults)) {
      const adultCount = parseInt(adults);
      for (let i = 0; i < adultCount; i++) {
        urlGuests.push({ ...DEFAULT_GUEST_CONFIG.adult, ageType: "adult" });
      }
    }

    if (teenagers !== null && isValidNumber(teenagers)) {
      const teenagerCount = parseInt(teenagers);
      for (let i = 0; i < teenagerCount; i++) {
        urlGuests.push({
          ...DEFAULT_GUEST_CONFIG.teenager,
          ageType: "teenager",
        });
      }
    }

    if (children) {
      const childGuests = parseGuestParams(children, "child");
      urlGuests.push(...childGuests);
    }

    if (infants) {
      const infantGuests = parseGuestParams(infants, "infant");
      urlGuests.push(...infantGuests);
    }

    if (urlGuests.length > 0) {
      // URL params map to active unit (legacy format).
      setUnits((prev) => {
        return prev.map((unit) =>
          unit.id === activeUnitId ? { ...unit, guests: urlGuests } : unit,
        );
      });
    }

    if (
      finalArrival &&
      finalDeparture &&
      isValidDate(finalArrival) &&
      isValidDate(finalDeparture) &&
      isValidDateRange(finalArrival, finalDeparture, isActivity) &&
      !hasCategoryAdDeepLink &&
      !suppressAutoSearch
    ) {
      setSearchInitiated(true);
    }

    if (shouldUpdateURL || suppressAutoSearch) {
      setTimeout(() => {
        const params = new URLSearchParams(window.location.search);
        if (finalArrival) {
          params.set(URL_PARAMS.ARRIVAL, finalArrival);
        }
        if (finalDeparture) {
          params.set(URL_PARAMS.DEPARTURE, finalDeparture);
        }
        // Consumed above — drop it so a copied link searches normally.
        params.delete(NO_AUTO_SEARCH_PARAM);
        const queryString = params.toString();
        const newURL = queryString
          ? `${window.location.pathname}?${queryString}`
          : window.location.pathname;
        window.history.replaceState({}, "", newURL);
      }, 0);
    }

    setIsUrlInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run-once bootstrap, gated by the ref above
  }, [isConfigLoading]);

  const getGuestsByType = useCallback(
    (ageType: AgeType): Guest[] =>
      guests.filter((guest) => guest.ageType === ageType),
    [guests],
  );

  const getGuestCount = useCallback(
    (ageType: AgeType): number => getGuestsByType(ageType).length,
    [getGuestsByType],
  );

  const availabilityParams = useMemo(() => {
    if (!wbeConfig?.siteId) return null;

    // Payment-link routes (/p/:slug and its sub-pages) never render the booking
    // calendar, so the availability data (strikethroughs, stop-times) is unused
    // there. Skip the query to avoid a wasted /api/public/availability/:siteId
    // request when a payment link is opened.
    if (window.location.pathname.split("/")[1] === "p") return null;

    const adultCount = getGuestCount("adult");
    if (adultCount === 0) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const firstMonthStart = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      1,
    );
    firstMonthStart.setHours(0, 0, 0, 0);

    // The Calendar renders a full week grid for each month: leading days from
    // the previous month and trailing days into the month after `nextMonth`
    // appear as padding. Those padding days must be queried too, otherwise
    // dates lacking pricing slip through and the user can click them.
    const gridStart = startOfWeek(firstMonthStart, { weekStartsOn: 1 });
    const from = today > gridStart ? today : gridStart;

    const nextMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      1,
    );
    const secondMonthEnd = new Date(
      nextMonth.getFullYear(),
      nextMonth.getMonth() + 1,
      0,
    );
    secondMonthEnd.setHours(23, 59, 59, 999);
    const gridEnd = endOfWeek(secondMonthEnd, { weekStartsOn: 1 });
    gridEnd.setHours(23, 59, 59, 999);

    return {
      siteId: wbeConfig.siteId,
      from: formatDateForAPI(from),
      to: formatDateForAPI(gridEnd),
      guestCount: {
        adults: getGuestCount("adult"),
        teenagers: getGuestCount("teenager"),
        children: getGuestCount("child"),
        infants: getGuestCount("infant"),
      },
    };
  }, [wbeConfig?.siteId, currentMonth, getGuestCount]);

  // Lodging-shaped: which dates can be an ARRIVAL for the searched units.
  // On an activity site there are no lodging ads, so this feed would flag
  // every date unavailable and strike out the whole search calendar — the
  // activity calendar constrains per ad instead (search results, ad view).
  const isActivitySite = isActivity;
  const {
    data: bookingAvailabilityData,
    isFetching: bookingAvailabilityFetching,
  } = useGetBookingAvailability({
    variables: availabilityParams!,
    enabled: !!availabilityParams && !isActivitySite,
  });

  const bookingAvailability = isActivitySite ? undefined : bookingAvailabilityData;

  const updateURLParams = useCallback(() => {
    const queryString = writeHeaderParams(window.location.search, {
      dates:
        selectedStartDate && selectedEndDate
          ? {
              arrival: formatLocalDate(selectedStartDate),
              departure: formatLocalDate(selectedEndDate),
            }
          : null,
      guests,
      languageCode: selectedLanguage,
      guestTypesConfig: wbeConfig?.guestTypesConfig,
    });
    // A half-picked range (start only) used to keep `arrival` in the URL. Keep that: write the
    // single date back so the header's own "one date set" bootstrap branch still works.
    const params = new URLSearchParams(queryString);
    if (selectedStartDate && !selectedEndDate)
      params.set(URL_PARAMS.ARRIVAL, formatLocalDate(selectedStartDate));
    if (!selectedStartDate && selectedEndDate)
      params.set(URL_PARAMS.DEPARTURE, formatLocalDate(selectedEndDate));
    const finalQuery = serializeParamsWithStableOrder(params);
    const newURL = finalQuery
      ? `${window.location.pathname}?${finalQuery}`
      : window.location.pathname;
    const currentURL = `${window.location.pathname}${window.location.search}`;
    if (newURL !== currentURL) {
      window.history.replaceState({}, "", newURL);
    }
  }, [
    selectedStartDate,
    selectedEndDate,
    guests,
    selectedLanguage,
    wbeConfig?.guestTypesConfig,
  ]);

  const serializeSearchParams = useCallback(
    (input: {
      base: string;
      dates: { arrival: string; departure: string } | null;
      guests: Guest[];
    }) =>
      writeHeaderParams(input.base, {
        dates: input.dates,
        guests: input.guests,
        languageCode: selectedLanguage,
        guestTypesConfig: wbeConfig?.guestTypesConfig,
      }),
    [selectedLanguage, wbeConfig?.guestTypesConfig],
  );

  const applySearchParams = useCallback(
    (search: string, unitId: string) => {
      const params = new URLSearchParams(search);
      const arrival = normalizeDateParam(params.get(URL_PARAMS.ARRIVAL)).value;
      const departure = normalizeDateParam(
        params.get(URL_PARAMS.DEPARTURE),
      ).value;
      const valid = validateUrlDates(arrival, departure, isActivity).isValid;
      setSelectedStartDate(
        valid && arrival ? new Date(`${arrival}T00:00:00`) : null,
      );
      setSelectedEndDate(
        valid && departure ? new Date(`${departure}T00:00:00`) : null,
      );

      const adults = params.get(URL_PARAMS.ADULTS);
      const teenagers = params.get(URL_PARAMS.TEENAGERS);
      const children = params.get(URL_PARAMS.CHILDREN);
      const infants = params.get(URL_PARAMS.INFANTS);
      if (
        adults === null &&
        teenagers === null &&
        children === null &&
        infants === null
      )
        return;

      const nextGuests: Guest[] = [];
      const count = (raw: string | null) =>
        raw !== null && isValidNumber(raw) ? parseInt(raw) : 0;
      for (let i = 0; i < count(adults); i++)
        nextGuests.push({ ...DEFAULT_GUEST_CONFIG.adult, ageType: "adult" });
      for (let i = 0; i < count(teenagers); i++)
        nextGuests.push({
          ...DEFAULT_GUEST_CONFIG.teenager,
          ageType: "teenager",
        });
      if (children) nextGuests.push(...parseGuestParams(children, "child"));
      if (infants) nextGuests.push(...parseGuestParams(infants, "infant"));
      // Mirrors the bootstrap: a 0-guest unit is a state pricing/availability
      // don't expect, so a param that resolves to nothing leaves the unit alone.
      if (nextGuests.length === 0) return;
      setUnits((prev) =>
        prev.map((unit) =>
          unit.id === unitId ? { ...unit, guests: nextGuests } : unit,
        ),
      );
    },
    [isActivity],
  );

  useEffect(() => {
    if (!isUrlInitialized) return;

    if (!hasSyncedInitialUrlRef.current) {
      hasSyncedInitialUrlRef.current = true;
      return;
    }

    updateURLParams();
  }, [
    selectedStartDate,
    selectedEndDate,
    units,
    activeUnitId,
    selectedLanguage,
    isUrlInitialized,
    updateURLParams,
  ]);

  // Top up every unit so each guest type meets the site-wide minimum once the
  // config is known. Covers guests seeded before the config loaded (defaults,
  // URL params) and any path that slips below the configured minimum.
  useEffect(() => {
    if (!wbeConfig?.maxConfig) return;
    const minGuests = wbeConfig.maxConfig.minGuests;

    setUnits((prev) => {
      let changed = false;
      const next = prev.map((unit) => {
        let guests = unit.guests;
        (Object.keys(AGE_TYPE_TO_CONFIG_KEY) as AgeType[]).forEach(
          (ageType) => {
            const min = minGuests[AGE_TYPE_TO_CONFIG_KEY[ageType]] ?? 0;
            const count = guests.filter((g) => g.ageType === ageType).length;
            if (count < min) {
              const resized = resizeGuestList(guests, ageType, min, wbeConfig);
              // resizeGuestList caps at max/total limits; only treat as a
              // change when the count actually grew, or the effect loops.
              const resizedCount = resized.filter(
                (g) => g.ageType === ageType,
              ).length;
              if (resizedCount !== count) {
                guests = resized;
                changed = true;
              }
            }
          },
        );
        return guests === unit.guests ? unit : { ...unit, guests };
      });
      return changed ? next : prev;
    });
  }, [wbeConfig, units]);

  const setGuestCountForUnit = useCallback(
    (unitId: string, ageType: AgeType, count: number) => {
      if (!wbeConfig) return;

      setUnits((prev) => {
        const next = [...prev];
        const idx = next.findIndex((u) => u.id === unitId);
        const safeIdx = idx >= 0 ? idx : 0;
        const unitGuests = next[safeIdx]?.guests ?? [];

        next[safeIdx] = {
          ...next[safeIdx],
          guests: resizeGuestList(unitGuests, ageType, count, wbeConfig),
        };
        return next;
      });
    },
    [wbeConfig],
  );

  const setGuestCount = useCallback(
    (ageType: AgeType, count: number) => {
      setGuestCountForUnit(activeUnitId, ageType, count);
    },
    [activeUnitId, setGuestCountForUnit],
  );

  const updateGuestForUnit = useCallback(
    (unitId: string, index: number, updates: Partial<Guest>) => {
      setUnits((prev) => {
        const next = [...prev];
        const idx = next.findIndex((u) => u.id === unitId);
        const safeIdx = idx >= 0 ? idx : 0;
        const prevGuests = next[safeIdx]?.guests ?? [];
        next[safeIdx] = {
          ...next[safeIdx],
          guests: prevGuests.map((guest, i) =>
            i === index ? { ...guest, ...updates } : guest,
          ),
        };
        return next;
      });
    },
    [],
  );

  const updateGuest = useCallback(
    (index: number, updates: Partial<Guest>) => {
      updateGuestForUnit(activeUnitId, index, updates);
    },
    [activeUnitId, updateGuestForUnit],
  );

  const getMaxGuestCount = useCallback(
    (ageType: AgeType): number => {
      if (!wbeConfig) return 0;
      return (
        wbeConfig.maxConfig.maxGuests[AGE_TYPE_TO_CONFIG_KEY[ageType]] || 0
      );
    },
    [wbeConfig],
  );

  const getBedTypeForAgeHelper = useCallback(
    (age: number): BedCapacityType => {
      if (!wbeConfig?.bedAgeSpanSettings) return "full";
      return getBedTypeForAge(age, wbeConfig.bedAgeSpanSettings);
    },
    [wbeConfig],
  );

  const resetHeader = useCallback(() => {
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    const id = createId();
    setUnits([{ id, guests: createDefaultGuests() }]);
    setActiveUnitId(id);
    setSearchInitiated(false);
    setOpenCalendarRequest(false);
    setCurrentMonth(new Date());

    // The booking code is part of the search, so a full reset drops it too —
    // clearing its sessionStorage as well so it can't outlive the dates. Only
    // the post-confirmation flow resets: a finished booking starts the next
    // guest from scratch. The logo deliberately does not, and F5 never hits
    // resetHeader either, so both of those keep code and dates alike (each
    // re-read from the URL/storage).
    useBookingCodeStore.getState().clearCode();

    // Clear URL parameters
    const newURL = window.location.pathname;
    window.history.replaceState({}, "", newURL);
  }, []);

  const addUnit = useCallback(() => {
    // Clone active unit guests (makes adding multiple units fast).
    const newId = createId();
    setUnits((prev) => {
      const active = prev.find((u) => u.id === activeUnitId) ?? prev[0];
      const newUnit: SearchUnit = {
        id: newId,
        guests: active ? [...active.guests] : createDefaultGuests(),
      };
      return [...prev, newUnit];
    });
    setActiveUnitId(newId);
  }, [activeUnitId]);

  const removeUnit = useCallback(
    (unitId: string) => {
      if (units.length <= 1) return;
      const next = units.filter((u) => u.id !== unitId);
      setUnits(next);
      if (!next.some((u) => u.id === activeUnitId)) {
        setActiveUnitId(next[0]!.id);
      }
    },
    [units, activeUnitId],
  );

  const replaceUnits = useCallback(
    (nextUnits: SearchUnit[]) => {
      if (!nextUnits.length) {
        const id = createId();
        setUnits([{ id, guests: createDefaultGuests() }]);
        setActiveUnitId(id);
        return;
      }

      setUnits(nextUnits);
      if (!nextUnits.some((unit) => unit.id === activeUnitId)) {
        setActiveUnitId(nextUnits[0]!.id);
      }
    },
    [activeUnitId],
  );

  const handleSetSelectedLanguage = useCallback(
    async (language: LanguageCode) => {
      if (selectedLanguage === language) return;
      const previousLanguage = selectedLanguage;
      const requestId = languageUpdateRequestIdRef.current + 1;
      languageUpdateRequestIdRef.current = requestId;
      const siteId = wbeConfig?.siteId;
      const shouldSaveLanguage =
        !!siteId &&
        hasAuthenticatedWBESession(
          queryClient.getQueriesData<WBEAuthSessionResponse>({
            queryKey: ["useWBEAuthSession"],
          }),
        );

      if (shouldSaveLanguage) {
        try {
          await updateLanguageMutation.mutateAsync({
            siteId,
            body: { languageCode: language },
          });
          setSelectedLanguage(language);
          localStorage.setItem("selectedLanguage", language);
          queryClient.setQueriesData<WBEAuthSessionResponse>(
            { queryKey: ["useWBEAuthSession"] },
            (current) => {
              if (!current?.authenticated) return current;
              return {
                ...current,
                account: { ...current.account, languageCode: language },
              };
            },
          );
        } catch {
          if (languageUpdateRequestIdRef.current !== requestId) return;
          setSelectedLanguage(previousLanguage);
          localStorage.setItem("selectedLanguage", previousLanguage);
          const previousI18nLanguage = getI18nLanguage(previousLanguage);
          if (previousI18nLanguage && i18n.language !== previousI18nLanguage) {
            i18n.changeLanguage(previousI18nLanguage);
          }
          toasty({ title: t("language.update_failed"), variant: "error" });
        }
      } else {
        setSelectedLanguage(language);
        localStorage.setItem("selectedLanguage", language);
      }
    },
    [
      selectedLanguage,
      t,
      updateLanguageMutation,
      wbeConfig?.siteId,
      queryClient,
    ],
  );

  const applySelectedLanguage = useCallback((language: LanguageCode) => {
    setSelectedLanguage(language);
    localStorage.setItem("selectedLanguage", language);
  }, []);

  // The stored choice outlives the site's settings. A guest who browsed in
  // German before the operator switched it off — or who carries it from
  // another site — must not be left on a language this one no longer serves.
  const offeredLanguages = wbeConfig?.languages;
  useEffect(() => {
    if (!offeredLanguages?.length) return;
    if (offeredLanguages.includes(selectedLanguage)) return;
    const fallback = pickOfferedLanguage(offeredLanguages);
    setSelectedLanguage(fallback);
    localStorage.setItem("selectedLanguage", fallback);
  }, [offeredLanguages, selectedLanguage]);

  useEffect(() => {
    const i18nLanguage = getI18nLanguage(selectedLanguage);
    if (i18nLanguage && i18n.language !== i18nLanguage) {
      i18n.changeLanguage(i18nLanguage);
    }
  }, [selectedLanguage]);

  return (
    <HeaderContext.Provider
      value={{
        selectedLanguage,
        openCalendarRequest,
        showMobileSearch,
        currentMonth,
        selectedStartDate,
        selectedEndDate,
        hasTodayArrivalDeeplink,
        units,
        activeUnitId,
        guests,
        searchInitiated,
        isUrlInitialized,
        serializeSearchParams,
        applySearchParams,
        wbeConfig: wbeConfig || undefined,
        isConfigLoading,
        bookingAvailability,
        bookingAvailabilityFetching,
        getMaxGuestCount,
        getBedTypeForAge: getBedTypeForAgeHelper,
        setSelectedLanguage: handleSetSelectedLanguage,
        applySelectedLanguage,
        setOpenCalendarRequest,
        setShowMobileSearch,
        setCurrentMonth,
        setSelectedStartDate,
        setSelectedEndDate,
        setGuestCount,
        setGuestCountForUnit,
        updateGuest,
        updateGuestForUnit,
        getGuestsByType,
        getGuestCount,
        setActiveUnitId,
        addUnit,
        removeUnit,
        replaceUnits,
        setSearchInitiated,
        updateURLParams,
        resetHeader,
      }}
    >
      {children}
    </HeaderContext.Provider>
  );
};

// oxlint-disable-next-line react/only-export-components -- 36 tests mock this module path for the hook
export const useHeader = () => {
  const context = useContext(HeaderContext);
  if (context === undefined) {
    throw new Error("useHeader must be used within a HeaderProvider");
  }
  return context;
};
